/** MIDI file upload and playback controls with loop and tempo support. */

import {
	FileMusic,
	Music,
	Play,
	Repeat,
	RotateCcw,
	Square,
	Upload,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import type {
	MidiFileInfo,
	MidiPlaybackHandle,
	MidiPlaybackOptions,
	ScheduledNote,
} from "../lib/midiPlayer.ts";
import { parseMidiFile, playMidiNotes } from "../lib/midiPlayer.ts";

/** Props for the MidiPlayer component. */
export type MidiPlayerProps = {
	readonly noteOn: (note: number) => void;
	readonly noteOff: (note: number) => void;
	readonly enabled: boolean;
	readonly allNotesOff: () => void;
};

/** A built-in demo MIDI file. */
type DemoMidi = {
	readonly label: string;
	readonly file: string;
};

/** Built-in demo MIDI files bundled with the app. */
const DEMO_MIDIS: readonly DemoMidi[] = [
	{ label: "Twinkle Twinkle", file: "twinkle-twinkle.mid" },
	{ label: "C Major Scale", file: "c-major-scale.mid" },
	{ label: "Chord Progression", file: "chord-progression.mid" },
] as const;

function formatTime(seconds: number): string {
	const m = Math.floor(seconds / 60);
	const s = Math.floor(seconds % 60);
	return `${m}:${s.toString().padStart(2, "0")}`;
}

export function MidiPlayer({
	noteOn,
	noteOff,
	enabled,
	allNotesOff,
}: MidiPlayerProps) {
	const [midiInfo, setMidiInfo] = useState<MidiFileInfo | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [progress, setProgress] = useState(0);
	const [duration, setDuration] = useState(0);
	const [loop, setLoop] = useState(false);
	const [tempoScale, setTempoScale] = useState(1.0);
	const notesRef = useRef<readonly ScheduledNote[]>([]);
	const handleRef = useRef<MidiPlaybackHandle | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	/** Effective BPM display. */
	const effectiveBpm = midiInfo
		? Math.round(midiInfo.originalBpm * tempoScale)
		: 120;

	const stopPlayback = useCallback(() => {
		handleRef.current?.stop();
		handleRef.current = null;
		setIsPlaying(false);
		setProgress(0);
		allNotesOff();
	}, [allNotesOff]);

	const handleFileUpload = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (!file) return;

			stopPlayback();

			const buffer = await file.arrayBuffer();
			const { notes, info } = parseMidiFile(buffer, file.name);
			notesRef.current = notes;
			setMidiInfo(info);
			setDuration(info.durationSeconds);

			// Reset file input so the same file can be re-selected
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		},
		[stopPlayback],
	);

	const handleLoadDemo = useCallback(
		async (demo: DemoMidi) => {
			stopPlayback();

			const response = await fetch(`midi/${demo.file}`);
			const buffer = await response.arrayBuffer();
			const { notes, info } = parseMidiFile(buffer, demo.label);
			notesRef.current = notes;
			setMidiInfo(info);
			setDuration(info.durationSeconds);
		},
		[stopPlayback],
	);

	const handlePlay = useCallback(() => {
		if (!enabled || notesRef.current.length === 0) return;

		// Stop existing playback
		stopPlayback();

		setIsPlaying(true);
		setProgress(0);

		const options: MidiPlaybackOptions = { loop, tempoScale };

		handleRef.current = playMidiNotes(
			notesRef.current,
			{
				noteOn,
				noteOff,
				onProgress(time, dur) {
					setProgress(time);
					setDuration(dur);
				},
				onFinished() {
					setIsPlaying(false);
					setProgress(0);
				},
			},
			options,
		);
	}, [enabled, noteOn, noteOff, loop, tempoScale, stopPlayback]);

	const handleStop = useCallback(() => {
		stopPlayback();
	}, [stopPlayback]);

	const handleUploadClick = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleBack = useCallback(() => {
		stopPlayback();
		setMidiInfo(null);
		notesRef.current = [];
	}, [stopPlayback]);

	const handleLoopToggle = useCallback(() => {
		const newLoop = !loop;
		setLoop(newLoop);
		handleRef.current?.setLoop(newLoop);
	}, [loop]);

	const handleTempoChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newScale = Number(e.target.value);
			setTempoScale(newScale);
			handleRef.current?.setTempoScale(newScale);
		},
		[],
	);

	return (
		<div className="midi-player">
			<div className="midi-header">
				<FileMusic size={14} />
				<span className="control-label" style={{ margin: 0 }}>
					MIDI
				</span>
			</div>

			<input
				ref={fileInputRef}
				type="file"
				accept=".mid,.midi"
				onChange={handleFileUpload}
				className="midi-file-input"
				aria-label="Upload MIDI file"
			/>

			{!midiInfo ? (
				<div className="midi-demo-list">
					{DEMO_MIDIS.map((demo) => (
						<button
							key={demo.file}
							type="button"
							className="midi-demo-btn"
							onClick={() => handleLoadDemo(demo)}
						>
							<Music size={12} />
							<span>{demo.label}</span>
						</button>
					))}
					<button
						type="button"
						className="midi-upload-btn"
						onClick={handleUploadClick}
					>
						<Upload size={14} />
						<span>Upload .mid</span>
					</button>
				</div>
			) : (
				<div className="midi-controls">
					<div className="midi-info">
						<span className="midi-name">{midiInfo.name}</span>
						<span className="midi-meta">
							{midiInfo.noteCount} notes &middot;{" "}
							{formatTime(midiInfo.durationSeconds)}
						</span>
					</div>

					<div className="midi-buttons">
						{isPlaying ? (
							<button
								type="button"
								className="midi-btn midi-btn-stop"
								onClick={handleStop}
								title="Stop"
							>
								<Square size={12} />
							</button>
						) : (
							<button
								type="button"
								className="midi-btn midi-btn-play"
								onClick={handlePlay}
								disabled={!enabled}
								title="Play"
							>
								<Play size={12} />
							</button>
						)}
						<button
							type="button"
							className={`midi-btn ${loop ? "midi-btn-loop-active" : ""}`}
							onClick={handleLoopToggle}
							title={loop ? "Loop: ON" : "Loop: OFF"}
						>
							<Repeat size={12} />
						</button>
						<button
							type="button"
							className="midi-btn"
							onClick={handleUploadClick}
							title="Upload new file"
						>
							<Upload size={12} />
						</button>
						<button
							type="button"
							className="midi-btn"
							onClick={handleBack}
							title="Back to file selection"
						>
							<RotateCcw size={12} />
						</button>
					</div>

					{/* Tempo control */}
					<div className="midi-tempo-control">
						<span className="midi-tempo-label">Tempo</span>
						<input
							type="range"
							min={0.25}
							max={3.0}
							step={0.05}
							value={tempoScale}
							onChange={handleTempoChange}
							className="midi-tempo-slider"
							aria-label="Tempo scale"
						/>
						<span className="midi-tempo-value">{effectiveBpm} BPM</span>
					</div>

					{(isPlaying || progress > 0) && duration > 0 && (
						<div className="midi-progress">
							<div
								className="midi-progress-bar"
								style={{ width: `${(progress / duration) * 100}%` }}
							/>
							<span className="midi-time">
								{formatTime(progress)} / {formatTime(duration)}
							</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
